import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import { t } from "i18next";
import CustomBreadCrumb from "../../../components/CustomBreadCrumb";
import { TOAST_TYPE, ACTIONS } from "../../../constants/Constants";
import toastContext from "../../../context/toast/ToastContext";
import themeContext from "../../../context/theme/ThemeContext";
import userContext from "../../../context/users/UserContext";
import Select from "react-select";
import { constantStyleForSelect } from "../../../components/CustomSelect";
import { addOrUpdateStyle, getStylesList } from "../../../service/MasterService";
import loaderContext from "../../../context/loader/LoaderContext";
import FroalaEditor from "react-froala-wysiwyg";
import "froala-editor/css/froala_style.min.css";
import "froala-editor/css/froala_editor.pkgd.min.css";

const customStyles = `
  .style-content-textarea {
    background-color: #ffffff !important;
    color: #000000 !important;
    width: 96.3% !important;
  }

  .style-content-textarea:focus {
    outline: none !important;
    box-shadow: none !important;
    border-color: inherit !important;
  }
`;

const AddStyle = ({ action = ACTIONS.ADD }) => {
  const { styleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getThemeColor, getAlterColor } = useContext(themeContext);
  const { roles, loadByUsername, authenticated } = useContext(userContext);
  const { showToastv2 } = useContext(toastContext);
  const { setLoaderRunning, ref } = useContext(loaderContext);
  const isEdit = action === ACTIONS.EDIT || location.pathname.includes("/edit/");
  const [isLoading, setIsLoading] = useState(true);
  const [reportTemplateOptions, setReportTemplateOptions] = useState([]);
  const [stylesData, setStylesData] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [styleContent, setStyleContent] = useState("");
  const [templateContent, setTemplateContent] = useState("");

  const [breadCrumbItems] = useState([
    {
      text: t("Agentic Report Styles"),
      key: "my_styles",
      onClick: () => window.location.href = "/my-style/1",
    },
    {
      text: isEdit ? t("Edit Style") : t("Add New Style"),
      key: isEdit ? "edit_style" : "add_style",
    },
  ]);

  const config = {
    placeholderText: "Edit Your Content Here!",
    key: process.env.REACT_APP_FROALA_API_KEY,
    charCounterCount: false,
    attribution: false,
    height: "18vh",
    width: "100%",
    fontSize: [
      "8",
      "9",
      "10",
      "10.67",
      "11",
      "12",
      "13",
      "13.33",
      "14",
      "14.67",
      "15",
      "16",
      "17",
      "17.33",
      "18",
      "18.67",
      "19",
      "21.33",
      "24",
      "30",
      "36",
      "48",
      "60",
      "72",
      "96",
    ],
    fontSizeDefault: "12",
    fontSizeUnit: 'px',
    lineHeights: {
      Default: '1.5',
      Single: '1',
      '1.15': '1.15',
      '1.5': '1.5',
      Double: '2'
    },
    wordPasteModal: false,
    pluginsEnabled: [
      "fullscreen",
      "codeBeautifier",
      "colors",
      "fontFamily",
      "fontSize",
      "paragraphFormat",
      "paragraphStyle",
      "align",
      "lists",
      "table",
      "wordPaste"
    ],
    toolbarButtons: {
      moreText: {
        buttons: [
          "undo",
          "redo",
          "|",
          "paragraphFormat",
          "|",
          "fontFamily",
          "|",
          "fontSize",
          "|",
          "bold",
          "italic",
          "underline",
          "strikeThrough",
          "clearFormatting",
          "|",
          "textColor",
          "backgroundColor",
          "|",
          "alignLeft",
          "alignCenter",
          "alignRight",
          "alignJustify",
          // 'inlineClass',
          // 'inlineStyle',
          "formatOL",
          "formatUL",
          "outdent",
          "indent",
          "|",
          "insertTable",
          "|",
          "copy",
        ],
        align: "left",
        buttonsVisible: 10,
      },
    },
  };

  useEffect(() => {
    if (!authenticated) {
      loadByUsername();
    }
  }, [authenticated]);

  useEffect(() => {
    const fetchData = async () => {
      // setLoaderMessage("Loading styles list...");
      setLoaderRunning(true);
      ref.current.continuousStart();
      try {
        const response = await getStylesList(isEdit ? 1 : 0);
        if (response.status === 200 && response.data.result === "SUCCESS") {
          setStylesData(response.data.data);

          const options = (response.data.data.reportTemplates || []).map(template => ({
            value: template,
            label: template
          }));
          setReportTemplateOptions(options);

          if (isEdit && styleId) {
            
            const style = response.data.data.styles.find(s => s.id === parseInt(styleId));

            if (style) {
              if (options.some(option => option.value === style.title)) {
                setSelectedTemplate({ value: style.title, label: style.title });
              }
              setStyleContent(style.styles);
              setTemplateContent(style.templateContent || "");
            } else {
              showToastv2(TOAST_TYPE.ERROR, "Style not found");
              navigate("/my-style/1");
            }
          }
        } else {
          showToastv2(TOAST_TYPE.ERROR, "Failed to fetch data");
          navigate("/my-style/1");
        }
      } catch (error) {
        showToastv2(TOAST_TYPE.ERROR, "Error fetching data");
        navigate("/my-style/1");
      } finally {
        setIsLoading(false);
        setLoaderRunning(false);
        ref.current.complete();
      }
    };

    fetchData();
  }, [styleId, isEdit]);

  useEffect(() => {
    if (isLoading) {
      setLoaderRunning(true);
      ref.current.continuousStart();
    } else {
      setLoaderRunning(false);
      ref.current.complete();
    }
    return () => {
      setLoaderRunning(false);
      ref.current.complete();
    };
  }, [isLoading]);

  const handleSubmit = async () => {
    const trimmedStyle = styleContent?.trim();

    if (!selectedTemplate) {
      showToastv2(TOAST_TYPE.ERROR, "Please select a report template");
      return;
    }

    if (!trimmedStyle && !templateContent) {
      showToastv2(TOAST_TYPE.ERROR, "Please fill at least Style or Template Content");
      return;
    }
    // setLoaderMessage(isEdit ? "Updating style..." : "Adding style...");
    setLoaderRunning(true);
    ref.current.continuousStart();

    try {
      if (!isEdit && stylesData) {
        const styleWithSameTitle = stylesData.styles.find(
          (style) => style.title === selectedTemplate.value
        );
        if (styleWithSameTitle) {
          showToastv2(TOAST_TYPE.ERROR, `A style for "${selectedTemplate.label}" already exists. Please edit the existing style.`);
          setLoaderRunning(false);
          ref.current.complete();
          return;
        }
      }

      const payload = {
        ...(isEdit && styleId && { id: parseInt(styleId) }),
        title: selectedTemplate.value,
        ...(trimmedStyle && { styles: trimmedStyle }),
        ...(templateContent && { templateContent: templateContent }),
      };

      const response = await addOrUpdateStyle(payload);

      if (response.data.result === "SUCCESS") {
        showToastv2(TOAST_TYPE.SUCCESS, response.data.message || (isEdit ? "Style updated successfully" : "Style created successfully"));
        setTimeout(() => {
          window.location.href = "/my-style/1";
        }, 500);
      } else {
        showToastv2(TOAST_TYPE.ERROR, response.data.message || `Failed to ${isEdit ? 'update' : 'add'} style`);
      }
    } catch (error) {
      showToastv2(TOAST_TYPE.ERROR, `Error ${isEdit ? 'updating' : 'adding'} style`);
    } finally {
      setLoaderRunning(false);
      ref.current.complete();
    }
  };

  if (isLoading) {
    return (
      <div className="main-container-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div className="breadCrumb">
          <CustomBreadCrumb items={breadCrumbItems} />
        </div>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          backgroundColor: getAlterColor("background-color")
        }}>
        </div>
      </div>
    );
  }

  return (
    <div className="main-container-body" style={{ display: 'flex', flexDirection: 'column', height: '100%' , backgroundColor: getAlterColor("background-color")}}>
      <div className="breadCrumb">
        <CustomBreadCrumb items={breadCrumbItems} />
      </div>
      <div className="report-template-content modal-scroll" style={{ flex: 1, overflowY: 'auto', padding: '10px 20px'}}>
        <div className="report-template-form" style={{ width: "70%" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: getAlterColor("background-color"),
              borderRadius: "4px",
            }}
          >
            <div style={{ width: "100%", padding: "10px 0px 0px 20px" }}>
              <section className="sectionA">
                <p style={{ fontSize: "20px", fontWeight: "600" }}>Style Details</p>
              </section>

              <div className="ms-Grid" dir="ltr">
                <div className="ms-Grid-row">
                  <div className="form-field ms-Grid-col ms-sm12 ms-md8 ms-lg4 ms-xl6">
                    <div className="report-template-list-key">Reporting Agents</div>
                    <Select
                      classNamePrefix="react-select"
                      options={reportTemplateOptions}
                      value={selectedTemplate}
                      styles={constantStyleForSelect(getThemeColor)}
                      onChange={setSelectedTemplate}
                      isDisabled={isEdit}
                    />
                  </div>
                </div>
              </div>

              <div style={{ padding: "10px", marginTop: "20px"}}>
                <div className="report-template-list-key" style={{ marginBottom: "10px" }}>
                  My prompts
                </div>
                <textarea
                  id="style-content"
                  name="style-content"
                  className="style-content-textarea"
                  value={styleContent}
                  onChange={(e) => setStyleContent(e.target.value)}
                  placeholder="Add Your Prompt Here."
                  style={{
                    height: "50px",
                    padding: "15px",
                    border: getThemeColor("border"),
                    borderRadius: "10px",
                    fontFamily: "Segoe UI",
                    fontSize: "14px",
                    resize: "none"
                  }}
                />
              </div>

              <div style={{ padding: "10px", marginTop: "10px" }}>
                <div className="report-template-list-key" style={{ marginBottom: "10px" }}>
                  Templates
                </div>
                <FroalaEditor
                  tag="textarea"
                  model={templateContent}
                  onModelChange={setTemplateContent}
                  config={config}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div 
        className="bottom-bar"
        style={{
          padding: '20px',
          display: 'flex',
          justifyContent: 'flex-start',
          gap: '10px',
          marginLeft: '40px',
          borderTop: `1px solid ${getThemeColor('border')}`
        }}
      >
        <DefaultButton
          text="Cancel"
          onClick={() => navigate("/my-style/1")}
          styles={{ root: { border: getThemeColor("border"), borderRadius: "7px" } }}
        />
        <PrimaryButton 
          text="Save"
          onClick={handleSubmit} 
          styles={{ root: { borderRadius: "7px" } }} 
        />
      </div>
      <style>{customStyles}</style>
    </div>
  );
};

export default AddStyle;